import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    doppePremiumFeatureIsDisabled,
    DoppePremiumFeaturesAlias
} from '../../client-server-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {asArray, hideableValueCreateHidden, immerProduce, HideableValue, hideableValueGetVisibleValue } from '@wix/devzai-utils-common';
import {
    DoppeActionTypeRenderingInPageSupport
} from "../../client-server-common/doppe-action-types/doppe-action-type-rendering-in-page-support";
import type {DoppeLinkSpec} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeLinkSpecCreateDefault,
    doppeLinkSpecResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-action-cta-button';
import {arrayRemoveNullValues} from '@wix/devzai-utils-common';
import {doppeLinkSpecResolveToUrl} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';


export namespace DoppeActionTypeBanner {

    export interface ActionSettings {
        /**
         * Link specification - can be a URL string, a page reference, or an entity reference.
         * Wrapped in DoppeHideableValue to allow hiding the link.
         */
        url: HideableValue<DoppeLinkSpec>;
        image: WixMediaResource | null;
    }
}

export const doppeActionTypeBanner = doppeActionTypeDefineMetadata<DoppeActionTypeBanner.ActionSettings>({
    id: 'io.bymo.action/banner',
    hasNoGoalMeasuring: false,
    isLinkOpener: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            image: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia)
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false,
        hideableContentSettingsNames: {
                url: true
            },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: false,
    hasNoLockInnerCTA: true,
    skipReportingOnGoalAchievedOnActionButtonClick: false,
    lockIsNotSupported: true,
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        url: hideableValueCreateHidden(doppeLinkSpecCreateDefault()),
        image: null,
    }),
    canRenderInPage: DoppeActionTypeRenderingInPageSupport.AsCardOnly,
    hasDisplaySettings: false,
    isPremiumAction: (premiumFeaturesStatus) => {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, DoppePremiumFeaturesAlias.bannerAction)
    },
    resolveUsedMedia: actionSettings => {
        return actionSettings.image ? asArray(actionSettings.image) : [];
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.image) {
                actionSettings.image = wixMediaResourceReplaceExternalImage(actionSettings.image, externalImagesUrlToImageResourceMap);
            }
        })
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.image
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const linkSpec = hideableValueGetVisibleValue(actionSettings.url, null);
        if (linkSpec === null) {
            return null;
        }
        
        // Use the common function to resolve DoppeLinkSpec to URL
        // This handles URL strings, Page links, and Entity links
        return doppeLinkSpecResolveToUrl(linkSpec, {
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: viewerLang
        }) ?? null;
    },
    resolveUsedBusinessEntities: actionSettings => {
        const linkSpec = hideableValueGetVisibleValue(actionSettings.url, null);
        if (linkSpec === null) {
            return [];
        }
        return arrayRemoveNullValues([
            doppeLinkSpecResolveBusinessEntityReference(linkSpec)
        ]);
    },
})